import { Typography } from '@mui/joy';
import { ReactNode, SyntheticEvent, useEffect, useRef } from 'react';
import { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { mapVendorLocationToFormContact } from 'companySettings/mappers';
import {
  IExtractorVendorMapping,
  IExtractorVendorMappingForm,
  IExtractorVendorMappingFormContact,
} from 'companySettings/types';
import { VendorContactFormField, VendorLocationFormField } from 'contacts/elements';
import { IContactGroupAddress } from 'contacts/types';
import { Form, FormField } from 'core/components';
import { InputType } from 'core/enums';
import { useFormContext } from 'core/states';
import { IAutocompleteOption } from 'core/types';
import { Color, Level } from 'theme/enums';

import { EXTRACTOR_ID_MAX_LENGTH } from './schema';
import { getContactLabel, isContactUnresolved } from '../utils';

interface IProps {
  extractorVendorMapping?: IExtractorVendorMapping;
  isOpen: boolean;
  onClose: (reset?: UseFormReset<IExtractorVendorMappingForm>) => void;
  onSubmit: (
    reset: UseFormReset<IExtractorVendorMappingForm>
  ) => (data: IExtractorVendorMappingForm) => Promise<void>;
  extraButtons?: ReactNode;
}

export const UpsertExtractorVendorMappingFormFields = ({
  extractorVendorMapping,
  isOpen,
  onClose,
  onSubmit,
  extraButtons,
}: IProps) => {
  // The contact THIS component suggested, by `contactId` — the key the field matches options on.
  // Anything else in the field was chosen by the user (on edit, the stored contact counts as
  // theirs) and must survive every later vendor change.
  const autoSelectedContactIdRef = useRef<number | null>(null);

  const { t } = useTranslation();

  const { getValues, setValue, watch } = useFormContext();

  const isEdit = Boolean(extractorVendorMapping?.id);

  // Storing such a contact is legal — the import just falls back to the manual prompt — so this
  // warns without blocking the save. It only ever fires on a stored mapping whose contact has since
  // lost its corporate user: a vendor pick cannot produce one, because the option's
  // `partfinitiContactId` is null whenever its `corporateUserId` is.
  const contactWarnings = isContactUnresolved(watch('contact'))
    ? [
        <Typography key="unresolvedContact" color={Color.WARNING} level={Level.BODY_SM}>
          {t('messageExtractorVendorMappingContactUnresolved')}
        </Typography>,
      ]
    : [];

  // The button renders this drawer unconditionally, so unlike the import prompt's vendor field it
  // is never unmounted between opens — without this, a suggestion made for one row would still be
  // "ours" when the next row is opened for editing.
  useEffect(() => {
    autoSelectedContactIdRef.current = null;
  }, [isOpen]);

  // The picked option carries the vendor site's resolved Partfiniti contact, so the pre-select is
  // synchronous — there is no request, and no window in which the pair on screen disagrees.
  const onChangeVendorLocation = (_: SyntheticEvent, option: IAutocompleteOption) => {
    const contact = getValues('contact') as IExtractorVendorMappingFormContact | null;
    const isOurSuggestion =
      Boolean(contact) && contact.contactId === autoSelectedContactIdRef.current;

    // An explicit choice is never overwritten — on edit the stored contact counts as one.
    if (contact && !isOurSuggestion) {
      return;
    }

    const prefilledContact = mapVendorLocationToFormContact(
      option as unknown as IContactGroupAddress
    );

    autoSelectedContactIdRef.current = prefilledContact?.contactId ?? null;

    if (prefilledContact) {
      setValue('contact', prefilledContact, { shouldValidate: true });

      return;
    }

    // The new vendor resolves nobody: drop what we suggested for the old one rather than leave it
    // next to a vendor it does not belong to. No `shouldValidate` — the contact is optional here,
    // and a cleared field is not a user error.
    if (isOurSuggestion) {
      setValue('contact', null);
    }
  };

  return (
    <Form
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={onSubmit}
      title={isEdit ? t('titleEditExtractorVendorMapping') : t('titleAddExtractorVendorMapping')}
      submitBtnLabel={isEdit ? t('buttonLabelSave') : t('buttonLabelAdd')}
      extraButtons={extraButtons}
    >
      <FormField
        name="extractorId"
        type={InputType.TEXT_INPUT}
        inputProps={{
          label: t('labelExtractorId'),
          required: true,
          slotProps: { input: { maxLength: EXTRACTOR_ID_MAX_LENGTH } },
          // Immutable once mapped — the edit payload does not carry it.
          disabled: isEdit,
        }}
      />

      <VendorLocationFormField
        name="vendorLocation"
        label={t('labelVendor')}
        placeholder={t('labelVendor')}
        onChange={onChangeVendorLocation}
      />

      <VendorContactFormField
        name="contact"
        label={t('labelVendorContact')}
        // The mapping stores the CRM `contact.id`, while the autocomplete option's own `id` is a
        // `corporate_user.id` — keying on `contactId` matches options against a stored mapping.
        optionKey="contactId"
        getLabel={(option: IAutocompleteOption) =>
          getContactLabel(option as IExtractorVendorMappingFormContact)
        }
        helperTexts={contactWarnings}
      />
    </Form>
  );
};
